import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { ChevronDown, Upload, Check, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/EmptyState';
import { useSectionPageEditor } from '@/hooks/useSectionPageEditor';

export interface TextFieldDef {
  key: string;
  label: string;
  /** Override the suffix-based long/short guess (e.g. a short-named
   * button field that happens to contain an embedded line break). */
  long?: boolean;
}

export interface ImageFieldDef {
  key: string;
  label: string;
}

export interface SectionDef {
  key: string;
  title: string;
  icon: LucideIcon;
  note?: string;
  images?: ImageFieldDef[];
  texts: TextFieldDef[];
  // Anything a page needs beyond images and texts (e.g. a video upload).
  extra?: ReactNode;
}

// Fields ending in one of these hold a full sentence/paragraph — rendered
// as a full-width textarea. Everything else is a short label/button/value
// — rendered as a compact input that flows several-per-row in the grid.
const LONG_FIELD_SUFFIXES = ['heading', 'subheading', 'subtitle', 'subline', 'body', 'tagline'];
function isLongField(key: string): boolean {
  const last = key.split('.').pop() ?? '';
  return LONG_FIELD_SUFFIXES.includes(last);
}

const LOCALES: { value: 'en' | 'ko' | 'bn'; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ko', label: '한국어' },
  { value: 'bn', label: 'বাংলা' },
];

function SectionToggle({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      aria-pressed={checked}
      aria-label={checked ? 'Section is visible — click to hide' : 'Section is hidden — click to show'}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
        checked ? 'bg-success' : 'bg-border'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// Every page's "premium" editor (Home, Brand Story, and everything after)
// renders through this one component — only the section config (and the
// pageKey/title) differ per page. Embeddable (no AppShell of its own) —
// it's rendered as one tab's content inside the single Website Settings
// hub page, which owns the one AppShell/sidebar for the whole admin.
export function SectionPageEditor({
  pageKey,
  title,
  subtitle,
  sections,
  defaultExpanded,
}: {
  pageKey: string;
  title: string;
  subtitle?: string;
  sections: SectionDef[];
  defaultExpanded?: string[];
}) {
  const {
    loading, locale, setLocale, isVisible, toggleSectionVisibility,
    fieldValue, handleFieldChange, handleFieldBlur, fieldStatus,
    imageFor, sizeHintFor, uploadingKey, uploadImage,
  } = useSectionPageEditor(pageKey);

  const [expanded, setExpanded] = useState<Set<string>>(new Set(defaultExpanded ?? [sections[0]?.key].filter(Boolean)));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingImageKeyRef = useRef<string | null>(null);

  const toggleExpanded = (sectionKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const triggerImageUpload = (key: string) => {
    pendingImageKeyRef.current = key;
    fileInputRef.current?.click();
  };
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const key = pendingImageKeyRef.current;
    e.target.value = '';
    if (!file || !key) return;
    uploadImage(key, file);
  };

  return (
    <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[18px] font-bold text-ink">{title}</h1>
            <p className="text-[12px] text-ink-faint mt-0.5">{subtitle ?? 'Every section, top to bottom — toggle visibility, replace images, edit text'}</p>
          </div>
          <div className="flex items-center gap-1 bg-surface-muted rounded-xl p-1">
            {LOCALES.map((l) => (
              <button
                key={l.value}
                onClick={() => setLocale(l.value)}
                className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold cursor-pointer transition-colors ${
                  locale === l.value ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleImageFileChange} />

        {loading ? (
          <Spinner />
        ) : (
          <div className="space-y-3">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const open = expanded.has(section.key);
              const visible = isVisible(section.key);
              return (
                <Card key={section.key} className={visible ? '' : 'opacity-60'}>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(section.key)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center rounded-[10px] bg-gradient-to-br from-primary-50 to-primary-100 text-primary-800 ring-1 ring-inset ring-primary-100 flex-shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[14px] font-bold text-ink tracking-tight truncate">
                          {index + 1}. {section.title}
                        </p>
                        {!visible && <p className="text-[11px] text-ink-faint">Hidden from the live page</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <SectionToggle checked={visible} onChange={(next) => toggleSectionVisibility(section.key, next)} />
                      <ChevronDown className={`w-4 h-4 text-ink-faint transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {open && (
                    <CardBody className="pt-0 space-y-5 border-t border-border-subtle">
                      {section.note && <p className="text-[11.5px] text-ink-faint italic pt-5">{section.note}</p>}

                      {section.extra && <div className={section.note ? '' : 'pt-5'}>{section.extra}</div>}

                      {section.images && section.images.length > 0 && (
                        <div className={section.note ? '' : 'pt-5'}>
                          <p className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-ink-faint mb-2">Images</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {section.images.map((img) => (
                              <div key={img.key} className="border border-border-subtle rounded-lg p-2.5 flex flex-col gap-2">
                                <div className="h-20 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden">
                                  {imageFor(img.key) ? (
                                    <img src={imageFor(img.key)} alt={img.label} className="max-h-full max-w-full object-contain" />
                                  ) : (
                                    <span className="text-[10px] text-ink-faint">No image set</span>
                                  )}
                                </div>
                                <p className="text-[11px] font-semibold text-ink truncate">{img.label}</p>
                                <p className="text-[10px] text-ink-faint leading-snug">{sizeHintFor(img.key)}</p>
                                <Button size="sm" variant="secondary" onClick={() => triggerImageUpload(img.key)} disabled={uploadingKey === img.key}>
                                  <Upload className="w-3 h-3" /> {uploadingKey === img.key ? 'Uploading…' : 'Replace'}
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className={section.images || section.note ? '' : 'pt-5'}>
                        <p className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-ink-faint mb-2">Text</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                          {section.texts.map((t) => {
                            const long = t.long ?? isLongField(t.key);
                            const status = fieldStatus[t.key];
                            return (
                              <div key={t.key} className={long ? 'sm:col-span-2 lg:col-span-3' : ''}>
                                <label className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5 text-ink-faint tracking-wide">
                                  {t.label}
                                  {status === 'saving' && <Loader2 className="w-3 h-3 animate-spin text-ink-faint" />}
                                  {status === 'saved' && <Check className="w-3 h-3 text-success" />}
                                </label>
                                {long ? (
                                  <Textarea
                                    rows={2}
                                    value={fieldValue(t.key)}
                                    onChange={(e) => handleFieldChange(t.key, e.target.value)}
                                    onBlur={() => handleFieldBlur(t.key)}
                                  />
                                ) : (
                                  <Input
                                    value={fieldValue(t.key)}
                                    onChange={(e) => handleFieldChange(t.key, e.target.value)}
                                    onBlur={() => handleFieldBlur(t.key)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardBody>
                  )}
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
